import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Link2, Image as ImageIcon, Wand2, Timer, CheckCircle2, AlertTriangle, RotateCcw, Plus, X, Type } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import ProductForm from "@/pages/seller/ProductForm";

const STEPS = ["Source", "Analyse IA", "Vérification", "Publication"];
const STAGES = ["Import de la source…", "Analyse de l'image et du contenu…", "Rédaction du titre et de la description…", "Détection des informations manquantes…"];
const MODES = [["upload", "Photo", Camera], ["page_url", "Lien de page", Link2], ["image_url", "Lien d'image", ImageIcon], ["text", "Quelques mots", Type]];

const Quota = ({ q }) => {
  if (!q.plan) {
    return <p data-testid="ai-quota-locked" className="rounded-xl bg-amber-50 p-4 text-sm font-semibold text-amber-900 ring-1 ring-amber-200">L'assistant IA est réservé aux boutiques ayant un abonnement actif ou un accès offert. <Link to="/vendeur/abonnement" className="underline">Voir les formules</Link></p>;
  }
  return <p data-testid="ai-quota" className="text-sm text-gray-600">Fiches IA ce mois-ci : <b>{q.used} / {q.limit}</b> (formule {q.plan === "premium" ? "Premium" : "Économie"}){q.plan !== "premium" && q.used >= q.limit ? " — passez au Premium pour en obtenir davantage." : ""}</p>;
};

const Stepper = ({ step }) => (
  <ol data-testid="ai-stepper" className="grid grid-cols-4 gap-2">
    {STEPS.map((s, i) => (
      <li key={s} className={`rounded-lg px-2 py-2 text-center text-xs font-bold ${i < step ? "bg-green-700 text-white" : i === step ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500"}`}>{i + 1}. {s}</li>
    ))}
  </ol>
);

const Chrono = ({ start, stopped }) => {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (stopped) return undefined;
    const i = setInterval(() => setT(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(i);
  }, [start, stopped]);
  const over = t > 240;
  return (
    <span data-testid="ai-chrono" className={`tnum flex items-center gap-1 rounded-full px-3 py-1 text-sm font-bold ${over ? "bg-amber-100 text-amber-800" : "bg-indigo-50 text-indigo-700"}`}>
      <Timer className="h-4 w-4" />{String(Math.floor(t / 60)).padStart(2, "0")}:{String(t % 60).padStart(2, "0")} / 04:00
    </span>
  );
};

const Analyzing = () => {
  const [p, setP] = useState(5);
  useEffect(() => {
    const i = setInterval(() => setP((x) => Math.min(92, x + (100 - x) * 0.06)), 600);
    return () => clearInterval(i);
  }, []);
  return (
    <div data-testid="ai-analyzing" className="space-y-4 rounded-2xl border bg-white p-6">
      <Progress value={p} className="h-2" />
      <p className="animate-pulse font-semibold text-indigo-700">{STAGES[Math.min(STAGES.length - 1, Math.floor(p / 25))]}</p>
      <div className="space-y-2">{[80, 60, 95, 40].map((w) => <div key={w} className="h-4 animate-pulse rounded bg-indigo-100" style={{ width: `${w}%` }} />)}</div>
    </div>
  );
};

const MAX_PHOTOS = 5;

const PhotoPicker = ({ files, setFiles, max, optional }) => {
  const ref = useRef();
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);
  const addFiles = (list) => {
    const merged = [...files, ...Array.from(list)];
    if (merged.length > max) toast.error(`${max} photos maximum par fiche`);
    setFiles(merged.slice(0, max));
  };
  return (
    <div data-testid="ai-photo-picker" className="space-y-2">
      {files.length === 0 ? (
        <button type="button" data-testid="ai-dropzone" onClick={() => ref.current.click()} className={`grid w-full place-items-center rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 p-4 text-indigo-700 ${optional ? "min-h-[90px]" : "min-h-[180px]"}`}>
          <span className="flex flex-col items-center gap-2 text-center text-sm font-semibold"><Camera className="h-8 w-8" />{optional ? "Facultatif : ajouter vos propres photos du produit" : `Prendre ou choisir 1 à ${max} photos du même produit`}<span className="text-xs font-normal">Face, dos, étiquette, détails… JPEG, PNG, WEBP, 8 Mo max par photo</span></span>
        </button>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {previews.map((u, i) => (
            <div key={u} className="relative aspect-square" data-testid={`ai-photo-${i}`}>
              <img src={u} alt="" className="h-full w-full rounded-lg object-cover" />
              {i === 0 && <span className="absolute bottom-1 left-1 rounded bg-gray-900/80 px-1.5 text-[10px] font-bold text-white">Principale</span>}
              <button type="button" data-testid={`ai-photo-remove-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 rounded-full bg-gray-900 p-0.5 text-white"><X className="h-3 w-3" /></button>
            </div>
          ))}
          {files.length < max && (
            <button type="button" data-testid="ai-photo-add" onClick={() => ref.current.click()} className="grid aspect-square place-items-center rounded-lg border-2 border-dashed border-indigo-200 text-indigo-600 hover:border-indigo-400"><Plus className="h-5 w-5" /></button>
          )}
        </div>
      )}
      <p data-testid="ai-photo-count" className="text-xs text-gray-500">{files.length} / {max} photo(s)</p>
      <input ref={ref} data-testid="ai-file-input" type="file" multiple accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
    </div>
  );
};

const SourcePicker = ({ onStart, locked }) => {
  const [mode, setMode] = useState("upload");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const urlMax = MAX_PHOTOS - 1;
  const ready = mode === "upload" ? files.length : mode === "text" ? text.trim().length >= 3 : url.trim();
  return (
    <div className="ai-glow rounded-2xl">
      <div className="space-y-5 rounded-2xl bg-white p-6">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {MODES.map(([m, label, I]) => (
            <button key={m} data-testid={`ai-mode-${m}`} onClick={() => setMode(m)} className={`flex flex-col items-center gap-1 rounded-xl p-3 text-sm font-semibold ring-1 ${mode === m ? "bg-indigo-600 text-white ring-indigo-600" : "ring-gray-200 hover:ring-indigo-300"}`}><I className="h-5 w-5" />{label}</button>
          ))}
        </div>
        {mode === "upload" ? (
          <PhotoPicker files={files} setFiles={setFiles} max={MAX_PHOTOS} />
        ) : mode === "text" ? (
          <>
            <Textarea data-testid="ai-text-input" rows={3} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ex : robe wax longue, coton, motifs orange et bleus, tailles S à XL" />
            <PhotoPicker files={files.slice(0, urlMax)} setFiles={setFiles} max={urlMax} optional />
          </>
        ) : (
          <>
            <Input data-testid="ai-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={mode === "page_url" ? "https://site.com/page-du-produit" : "https://site.com/image.jpg"} />
            <PhotoPicker files={files.slice(0, urlMax)} setFiles={setFiles} max={urlMax} optional />
          </>
        )}
        <p className="text-xs text-gray-500">L'IA prépare un brouillon. Elle n'invente ni prix ni caractéristiques et ne publie jamais à votre place. Chaque analyse consomme un peu du solde de la clé IA Casadeal ; chaque photo ajoutée augmente légèrement ce coût.</p>
        <Button size="lg" className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="ai-generate-submit"
          disabled={locked || !ready} onClick={() => onStart(mode, mode === "text" ? "" : url.trim(), mode === "upload" ? files : files.slice(0, urlMax), mode === "text" ? text.trim() : "")}>
          <Wand2 className="mr-2 h-4 w-4" />Générer la fiche
        </Button>
      </div>
    </div>
  );
};

export default function AiWizard() {
  const [step, setStep] = useState(0);
  const [start, setStart] = useState(Date.now());
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [stoppedAt, setStoppedAt] = useState(null);
  const nav = useNavigate();
  const { data: quota, reload: reloadQuota } = useApi("/seller/ai/quota");

  const run = async (mode, url, files, text) => {
    setError(null);
    setStep(1);
    const fd = new FormData();
    fd.append("mode", mode);
    if (url) fd.append("url", url);
    if (text) fd.append("text", text);
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/seller/ai/draft", fd, { timeout: 120000 });
      setResult(data);
      setStep(2);
    } catch (e) {
      setError(errMsg(e));
      setStep(0);
    }
    reloadQuota();
  };

  const submit = async (payload) => {
    await api.post("/seller/products", payload);
    setStoppedAt(Math.floor((Date.now() - start) / 1000));
    setStep(3);
    toast.success(payload.status === "published" ? "Produit publié" : "Brouillon enregistré");
  };

  const d = result?.draft;
  const initial = d && {
    title: d.title || "", description: d.description || "", category_slug: d.category_slug || "", subcategory: d.subcategory || "",
    features: d.features || [], keywords: d.keywords || [], variants: d.variants || [], sales_pitch: d.sales_pitch || "",
    images: result.images.map((i) => i.url), ai_generated: true,
  };
  const reset = () => { setStep(0); setResult(null); setStart(Date.now()); setStoppedAt(null); };

  return (
    <div data-testid="ai-wizard" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 font-display text-3xl font-black tracking-tight"><Wand2 className="h-7 w-7 text-indigo-600" />Créer avec l'IA</h1>
        <Chrono start={start} stopped={step === 3} />
      </div>
      <Stepper step={step} />
      {error && <p data-testid="ai-error" className="flex items-center gap-2 rounded-xl bg-red-50 p-4 text-sm text-red-700 ring-1 ring-red-200"><AlertTriangle className="h-4 w-4" />{error}</p>}
      {step === 0 && quota && <Quota q={quota} />}
      {step === 0 && <SourcePicker onStart={run} locked={!quota?.plan || quota.used >= quota.limit} />}
      {step === 1 && <Analyzing />}
      {step === 2 && d && (
        <>
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
            <span data-testid="ai-confidence" className="rounded-full bg-indigo-50 px-3 py-1 font-semibold text-indigo-700">Confiance de l'IA : {d.confidence || "—"}</span>
            <span>Analyse en {result.duration_s} s</span>
            <span data-testid="ai-photos-analyzed">{result.images.length} photo(s) analysée(s)</span>
            <Button variant="ghost" size="sm" onClick={reset} data-testid="ai-restart"><RotateCcw className="mr-1 h-3 w-3" />Recommencer</Button>
          </div>
          {d.test_mode && <p data-testid="ai-test-mode-banner" className="rounded-xl bg-indigo-50 p-3 text-sm font-semibold text-indigo-800 ring-1 ring-indigo-200">MODE TEST : brouillon fictif, aucune IA réelle appelée et aucun crédit IA consommé. Ce brouillon compte toutefois dans votre quota mensuel de fiches IA.</p>}
          {d.warnings?.length > 0 && <div data-testid="ai-warnings" className="rounded-xl bg-gray-50 p-3 text-sm text-gray-700">{d.warnings.map((w) => <p key={w}>• {w}</p>)}</div>}
          <ProductForm initial={initial} unknown={["Prix de vente", "Stock disponible", ...(d.unknown_fields || [])]} externalImage={result.images.some((i) => i.source === "externe")} onSubmit={submit} />
        </>
      )}
      {step === 3 && (
        <div data-testid="ai-done" className="space-y-4 rounded-2xl border bg-white p-8 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-green-700" />
          <p className="font-display text-2xl font-black">Fiche enregistrée en {Math.floor(stoppedAt / 60)} min {stoppedAt % 60} s</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" className="rounded-full" onClick={reset} data-testid="ai-another">Créer une autre fiche</Button>
            <Button className="rounded-full" onClick={() => nav("/vendeur/produits")} data-testid="ai-go-products">Voir mes produits</Button>
          </div>
        </div>
      )}
    </div>
  );
}
